import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  AlarmClock,
  FileText,
  Repeat,
  Send,
  SlidersHorizontal,
  Users,
  Waypoints,
} from "lucide-react";
import { useTranslate } from "ra-core";
import { Link, useLocation } from "react-router-dom";

// paths.ts imports nothing, so importing it here cannot start the cycle that
// moved these constants out of mobile.tsx in the first place.
//
// CONTACTS_ROOT, isAtOrBelow (which was `matchesScreen` here), ownedTab and
// the OWNED_ELSEWHERE list behind it all lived in THIS file until the mobile
// bottom bar needed the same rule. They moved to the leaf module so there is
// exactly one copy and one Node test of it; isContactsSection there records
// what a second copy beside a second lamp would cost.
import {
  CONTACTS_ROOT,
  isAtOrBelow,
  isContactsSection,
  MARKETING_PATHS,
  ownedTab,
} from "./paths";

/**
 * The Contacts sub-navigation: a second bar under the header, on every
 * /contacts page.
 *
 * WHY THIS IS NOT A SHELL COMPONENT, DESPITE THE FILE NAME
 * -------------------------------------------------------
 * PipelineShell wraps each pipeline route, so it can own the column its tabs
 * and its content share. This bar cannot: it is rendered once by
 * layout/Header.tsx and sits above whatever route is showing -- including
 * upstream's own contact list and contact page, which this fork does not wrap
 * and should not start wrapping just to draw a tab bar. So there is no
 * wrapper here, and screens under /contacts need no width of their own;
 * layout/Layout.tsx already gives every page one.
 *
 * An injection into an upstream file is the cost, which is why it is ONE line
 * naming a component we own, guarded by check-injections.mjs like the others.
 * See mobile.tsx for the contract. This file now owns TWO of them:
 * ContactsNavTabs in layout/Header.tsx, and ContactsNavButton -- at the bottom
 * of this file -- in layout/MobileNavigation.tsx.
 *
 * DESKTOP ONLY. MobileLayout renders no header at all, so this bar does not
 * exist on a phone, and squeezing a second row of tabs onto a 61px bottom bar
 * was never the answer. The phone reaches the same seven screens through
 * ContactsNavButton at the bottom of this file: one bottom-bar slot with the
 * same CONTACTS_SCREENS array behind it as a menu.
 */

/**
 * The seven Contacts destinations, in the order they are shown.
 *
 * ONLY THE ONES WHOSE ROUTES EXIST. A tab pointing at an unregistered path is
 * a link to a 404 in the header of every contacts page, so each entry landed
 * in the same commit as its own route in mobile.tsx -- Content, Follow-ups,
 * Sequences, and now Outbound, whose mk-outbound route went in alongside this
 * line rather than after it.
 *
 * EXPORTED so there is exactly ONE list. It drives the desktop tab strip
 * below, ContactsNavButton's mobile menu at the bottom of this file, and
 * mobileNav.test.tsx's assertion about that menu -- so a screen added here
 * appears in all three with no second edit, and a test that only ever saw six
 * of seven cannot pass. PIPELINE_SCREENS in mobile.tsx is exported for the
 * same reason and drives the same three things on the pipeline side.
 */
export const CONTACTS_SCREENS = [
  {
    path: CONTACTS_ROOT,
    label: "All contacts",
    Icon: Users,
  },
  {
    path: MARKETING_PATHS.followups,
    label: "Follow-ups",
    Icon: AlarmClock,
  },
  {
    // The picker at /contacts/lifecycle. A contact's OWN page lives at
    // /contacts/:id/lifecycle and does not light this tab -- isAtOrBelow()
    // is a prefix test and those two paths share no prefix but /contacts, so
    // the fallback claims it. That is the same answer /contacts/1/touches
    // gets and it is the right one: a page about one person is "a contact"
    // first. Changing it would mean giving CONTACTS_SCREENS a second kind of
    // match, for a highlight.
    path: MARKETING_PATHS.lifecycle,
    label: "Lifecycle",
    Icon: Waypoints,
  },
  {
    // The cadence settings the Lifecycle tab above obeys: how long each stage
    // lets somebody sit untouched, what the triage task should say, and the
    // order the stages appear in.
    //
    // ITS PATH IS A SIBLING OF /contacts/lifecycle, NOT A CHILD. isAtOrBelow
    // below is a prefix test and activePath takes the FIRST entry that matches,
    // so /contacts/lifecycle/stages would light Lifecycle and leave this tab
    // dark for ever. /contacts/lifecycle-stages shares no prefix with it, so
    // each tab claims exactly its own screen. See paths.ts.
    path: MARKETING_PATHS.lifecycleStages,
    label: "Stages",
    Icon: SlidersHorizontal,
  },
  {
    path: MARKETING_PATHS.contactContent,
    label: "Content",
    Icon: FileText,
  },
  {
    path: MARKETING_PATHS.sequences,
    label: "Sequences",
    Icon: Repeat,
  },
  {
    path: MARKETING_PATHS.outbound,
    label: "Outbound",
    Icon: Send,
  },
];

/**
 * Which tab is lit -- exactly one, always.
 *
 * "All contacts" is the fallback rather than a prefix match on /contacts,
 * because that prefix covers every other tab and would light two at once. As
 * the fallback it also correctly claims the pages that belong to no tab: a
 * contact's own page at /contacts/1/show and their touch history at
 * /contacts/1/touches are both "a contact", and the bar should say so rather
 * than go blank on them.
 */
const activePath = (pathname: string) =>
  // A resource route the library owns lights the library's tab. Checked FIRST:
  // /content_assets/5 matches no CONTACTS_SCREENS entry at all, so without
  // this it would fall through to the "All contacts" default and light the
  // wrong tab on a page that is plainly about a template.
  ownedTab(pathname) ??
  CONTACTS_SCREENS.find(
    ({ path }) => path !== CONTACTS_ROOT && isAtOrBelow(pathname, path),
  )?.path ??
  CONTACTS_ROOT;

/**
 * Rendered unconditionally by layout/Header.tsx, and decides for itself
 * whether it belongs on this page -- so the injection stays one line with no
 * condition for a merge to mangle.
 */
export const ContactsNavTabs = () => {
  const { pathname } = useLocation();
  // THE SAME PREDICATE THE MOBILE SLOT LIGHTS ON, imported rather than spelled
  // out twice. It was `!isAtOrBelow(pathname, CONTACTS_ROOT) &&
  // ownedTab(pathname) === null` here, which is isContactsSection negated --
  // and two copies of it is how this bar and that slot come to disagree about
  // whether /content_assets/5 is a contacts page.
  if (!isContactsSection(pathname)) return null;
  const active = activePath(pathname);

  return (
    /**
     * The SAME container as <main> in layout/Layout.tsx -- max-w-screen-xl,
     * centered, px-4 -- and deliberately not PipelineShell's narrower
     * max-w-5xl. PipelineShell owns both its tabs and its content, so it can
     * pick any width and they stay aligned. This bar owns neither, so its
     * only way to share a left edge with the page below is to repeat that
     * page's container. When the pipeline tabs got this wrong they began
     * several hundred pixels left of their content and read as page furniture
     * rather than as that content's own tabs.
     *
     * pt-4 MIRRORS <main>'s OWN TOP PADDING, and is the reason this bar sat
     * higher than the pipeline's. PipelineShell wraps the routed content, so
     * it renders INSIDE <main> and inherits that pt-4 above its tabs. This bar
     * is rendered by Header.tsx, outside <main>, so it had nothing above it
     * and the two sub-navigations sat 16px apart on otherwise identical pages.
     * If <main>'s padding changes in layout/Layout.tsx, this must follow it.
     */
    <div className="max-w-screen-xl mx-auto px-4 pt-4">
      <div className="flex gap-1 overflow-x-auto border-b">
        {CONTACTS_SCREENS.map(({ path, label, Icon }) => (
          <Link
            key={path}
            to={path}
            className={cn(
              "flex items-center gap-1.5 px-3 py-2 text-sm whitespace-nowrap border-b-2 transition-colors",
              path === active
                ? "border-foreground font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className="size-4" />
            {label}
          </Link>
        ))}
      </div>
    </div>
  );
};

/**
 * Bottom-bar entry for the Contacts section. ONE slot, with all seven screens
 * behind a menu.
 *
 * WHY THIS REPLACES AN UPSTREAM BUTTON RATHER THAN LIVING BESIDE ONE
 * -----------------------------------------------------------------
 * Every other injection this fork makes ADDS something upstream does not have.
 * This one does not: layout/MobileNavigation.tsx already renders
 * `<NavigationButton href="/contacts" ... />`, and the phone already has a
 * Contacts slot. What it does not have is any way to reach the six screens
 * hanging off that section -- ContactsNavTabs above is the only route to them
 * and MobileLayout renders no header at all, so on a phone Follow-ups,
 * Lifecycle, Stages, Content, Sequences and Outbound were unreachable
 * entirely. Not awkward to reach: absent.
 *
 * THE SLOT COUNT DOES NOT CHANGE. This is a swap, not an addition -- the bar
 * still holds six children, and every geometry assertion in mobileNav.test.tsx
 * is unchanged because the class string below matches upstream's
 * NavigationButton class for class. That match is load-bearing rather than
 * tidy: this button sits in the SAME flex row as upstream's, and a slot that
 * sizes differently re-creates the clip at both ends of the bar that c2c12ea4
 * was written to fix. See the long comment on that class string in
 * layout/MobileNavigation.tsx.
 *
 * WHY IT COULD NOT LIVE IN OUR OWN FILE, WHICH IS THE COST OF THIS CHANGE.
 * MarketingNavButton is a line ADDED to upstream's row; this is a line
 * SWAPPED in it, so it is a second, different injection into the same file
 * and check-injections.mjs gains a needle for it. There is no version of this
 * that stays out of upstream's file: NavigationButton is upstream's, is not
 * exported, and the slot being replaced is one it renders itself. The
 * alternative -- leaving upstream's button and adding a seventh slot for the
 * sub-screens -- is the seven-slot bar c2c12ea4 and 34205c91 measured and
 * rejected, at 35.4px against Apple's 44pt guidance.
 *
 * DRIVEN BY `CONTACTS_SCREENS`, for the reason MarketingNavButton is driven by
 * PIPELINE_SCREENS: a hand-written second list is a list that goes stale.
 * Adding a Contacts screen would add its tab and silently not add its menu
 * entry, and nothing would fail. mobileNav.test.tsx derives its expectation
 * from the same array rather than counting to seven.
 *
 * MEASURED, in mobileNav.test.tsx, and the point of measuring is that NOTHING
 * MOVED: at 320px the six slots are 54.4 49.4 [68 FAB] 49.4 49.4 49.4 = 320
 * both before and after this swap, at 390px every slot is 64.4, and at 767px
 * every slot caps at 68 with the cluster centered. The menu at 320x700 settles
 * at 164.0 x 367.5, top 267.0, bottom 634.5 against a bar top of 638.7 --
 * upward, wholly on screen, and scrollHeight == clientHeight, so all seven
 * entries are showing rather than a clipped scroller.
 *
 * "ALL CONTACTS" IS THE MENU'S FIRST ITEM, not a special case beside it. It is
 * already the first CONTACTS_SCREENS entry, at CONTACTS_ROOT, so the plain
 * contact list is one tap into the menu exactly as Overview is in the pipeline
 * menu. That is one tap MORE than before for the app's most-tapped
 * destination, and it is the honest cost of the trade: six screens go from
 * unreachable to one tap, the list goes from one tap to two.
 */
export const ContactsNavButton = () => {
  const { pathname } = useLocation();
  const translate = useTranslate();
  // NOT `pathname.startsWith("/contacts")`, AND THIS IS THE WHOLE TRAP. The
  // Content library is at /contacts/content but its rows are resource records:
  // `rowClick="edit"` goes to /content_assets/5, which shares no prefix with
  // /contacts. A prefix test here puts out the Contacts lamp on a page that is
  // plainly a contacts page -- the same bug the desktop tab strip shipped,
  // where the sub-navigation vanished on opening a template and the only way
  // back was the browser's back button.
  //
  // isContactsSection is that rule, in paths.ts, shared with ContactsNavTabs
  // above rather than copied. See it there.
  const on = isContactsSection(pathname);

  // Shared by every destination in the menu, and matched to the create FAB's
  // menu in layout/MobileNavigation.tsx and to MarketingNavButton's class for
  // class: three menus opening out of the same 61px bar that sized their rows
  // differently would read as three applications.
  const item = "h-12 px-4 text-base";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            // MUST MATCH NavigationButton in layout/MobileNavigation.tsx,
            // class for class -- this replaces one of its instances and sits
            // in the same flex row as the rest. The reasoning for every class
            // is in that file; the short version is no fixed width, `basis-0
            // grow` to share the row out, `has-[>svg]:px-0` to cancel the base
            // size's `has-[>svg]:px-3`, and NO `min-w-0` so a nowrap label can
            // never be clipped or pushed onto its neighbor.
            "flex-col gap-1 h-auto py-2 has-[>svg]:px-0 rounded-md basis-0 grow max-w-16",
            on ? null : "text-muted-foreground",
          )}
        >
          <Users className="size-6" />
          {/*
            UPSTREAM'S OWN LABEL, through upstream's own translation key, so
            the swapped slot is not the one word in the bar that stops being
            translated. It is also what mobileNav.test.tsx's LABELS sentinel
            reads, so a change here is a failing test rather than a bar that
            quietly says something else.
          */}
          <span className="text-[0.6rem] font-medium">
            {translate("resources.contacts.name", { smart_count: 2 })}
          </span>
        </Button>
      </DropdownMenuTrigger>
      {/*
        UPWARD, EXPLICITLY, for the reason MarketingNavButton's identical prop
        records at length: the bar is `fixed bottom-0`, so Radix's default
        `side="bottom"` asks to be drawn below the bottom of the viewport and
        only collision handling saves it. Same mutation, same result -- MB9
        there showed the request is what wins once the correction is not there.
      */}
      <DropdownMenuContent side="top">
        {CONTACTS_SCREENS.map(({ path, label, Icon }) => (
          <DropdownMenuItem key={path} asChild className={item}>
            {/*
              A real <Link>, not an `onSelect` that navigates: these are
              destinations, so they get an anchor -- which is also what makes
              them long-pressable and openable in a new tab.
            */}
            <Link to={path}>
              <Icon className="size-4" />
              {label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
